<template>
    <div>
        <div 
            class="flex items-center justify-between cursor-pointer mb-4 group" 
            @click="isOpen = !isOpen"
        >
            <h3 class="text-lg font-medium">Local Rewards</h3>
            <i 
                class="fas fa-angle-down transition-transform duration-200 text-gray-400 group-hover:text-gray-600" 
                :class="{ 'transform rotate-180': isOpen }"
            ></i>
        </div>
        
        <div v-show="isOpen" class="transition-all duration-200">
            <div class="bg-white shadow-lg rounded-sm border border-slate-200">
                <header class="px-5 py-4">
                    <!-- Removed -->
                </header>
                <div v-if="loading" class="text-center p-4">
                    Loading rewards...
                </div>
                <div v-else-if="rewards && rewards.length > 0" class="p-3">
                    <div class="overflow-x-auto">
                        <table class="table-auto w-full">
                            <thead class="text-xs uppercase text-slate-400 bg-slate-50 rounded-sm">
                                <tr>
                                    <th class="p-2 whitespace-nowrap">
                                        <div class="font-semibold text-left">Card</div>
                                    </th>
                                    <th class="p-2 whitespace-nowrap">
                                        <div class="font-semibold text-left">Balance</div>
                                    </th>
                                    <th class="p-2 whitespace-nowrap">
                                        <div class="font-semibold text-left">Created</div>
                                    </th>
                                    <th class="p-2 whitespace-nowrap">
                                        <div class="font-semibold text-left">Updated</div>
                                    </th>
                                    <th class="p-2 whitespace-nowrap">
                                        <div class="font-semibold text-left">Use for Rewards</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="text-sm divide-y divide-slate-100">
                                <tr v-for="reward in rewards" :key="reward.id">
                                    <td class="p-2 whitespace-nowrap">
                                        <div class="text-left">
                                            <template v-if="getConnectedAccount(reward)">
                                                {{ getCardName(reward) }}
                                                <span v-if="getLastFour(reward)" class="text-slate-500">
                                                    ({{ getLastFour(reward) }})
                                                </span>
                                            </template>
                                            <span v-else class="text-slate-400">-</span>
                                        </div>
                                    </td>
                                    <td class="p-2 whitespace-nowrap">
                                        <div class="text-left">{{ formatNumber(reward.balance) }} {{ reward.description }}</div>
                                    </td>
                                    <td class="p-2 whitespace-nowrap">
                                        <div class="text-left">{{ formatDate(reward.created_on) }}</div>
                                    </td>
                                    <td class="p-2 whitespace-nowrap">
                                        <div class="text-left">{{ formatDate(reward.updated_at) }}</div>
                                    </td>
                                    <td class="p-2 whitespace-nowrap">
                                        <div class="text-left">
                                            <input 
                                                type="checkbox" 
                                                :checked="getUseForRewards(reward)"
                                                @change="updateUseForRewards(reward, $event)"
                                                class="form-checkbox h-4 w-4 text-blue-600"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-else class="p-3">
                    <div class="text-sm text-slate-500 p-2">No local rewards data found.</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { Apii } from '../../services/api'

const api = new Apii()

export default {
    name: 'LocalRewards',
    props: {
        userid: {
            type: [Number, String],
            required: true
        }
    },
    setup(props) {
        const rewards = ref([])
        const loading = ref(true)
        const error = ref(null)
        const isOpen = ref(false)

        const fetchRewards = async () => {
            try {
                loading.value = true
                const response = await api.apiCall(
                    `/admin/users/${props.userid}/local-rewards`,
                    'get',
                    {},
                    {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                    }
                )
                rewards.value = response.data
            } catch (err) {
                console.error('Error fetching local rewards:', err)
                error.value = 'Failed to load local rewards data'
            } finally {
                loading.value = false
            }
        }

        const getConnectedAccount = (reward) => {
            return reward.connected_accounts && reward.connected_accounts.length ? reward.connected_accounts[0] : null
        }

        const getCardName = (reward) => {
            const account = getConnectedAccount(reward)
            return account && account.card ? account.card.title : '-'
        }

        const getLastFour = (reward) => {
            const account = getConnectedAccount(reward)
            return account ? account.last_four : null
        }

        const getUseForRewards = (reward) => {
            const account = getConnectedAccount(reward)
            return account ? account.use_for_rewards : false
        }

        const updateUseForRewards = async (reward, event) => {
            try {
                const useForRewards = event.target.checked
                await api.apiCall(
                    `/admin/users/${props.userid}/local-rewards/use-for-rewards`,
                    'post',
                    {
                        account_guid: reward.account_guid,
                        use_for_rewards: useForRewards
                    },
                    {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                    }
                )
                
                // Update the local state to reflect the change
                if (reward.connected_accounts && reward.connected_accounts.length) {
                    reward.connected_accounts[0].use_for_rewards = useForRewards
                }
            } catch (err) {
                console.error('Error updating use for rewards:', err)
                // Revert the checkbox if the update failed
                event.target.checked = !event.target.checked
            }
        }

        const formatDate = (date) => {
            if (!date) return ''
            return new Date(date).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
        }

        const formatNumber = (number) => {
            return new Intl.NumberFormat().format(number)
        }

        onMounted(() => {
            fetchRewards()
        })

        return {
            rewards,
            loading,
            error,
            isOpen,
            formatDate,
            formatNumber,
            getUseForRewards,
            updateUseForRewards,
            getConnectedAccount,
            getCardName,
            getLastFour
        }
    }
}
</script>

<style scoped>
.form-checkbox {
    border-radius: 0.25rem;
    border-color: #d1d5db;
    cursor: pointer;
}
.form-checkbox:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
}
</style>
